import React, { useEffect, useState } from "react";
import { Unit, UnitDimension, UnitSystem } from "../../types/Unit";
import {
    getAllUnits,
    createUnit,
    updateUnit,
    deleteUnit,
    getAllUnitDimensions,
    getAllUnitSystems,
    convertUnit,
} from "../../api/UnitAPI";
import { UnitConversionResult } from "../../types/Unit";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;
}

/** Parse a numeric form field; blank -> undefined, invalid -> NaN (caller validates). */
function parseNum(s: string): number | undefined {
    const t = s.trim();
    if (t === "") return undefined;
    return Number(t);
}

const UnitSection: React.FC<Props> = ({ token }) => {
    const [items, setItems] = useState<Unit[]>([]);
    const [dimensions, setDimensions] = useState<UnitDimension[]>([]);
    const [systems, setSystems] = useState<UnitSystem[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Filter
    const [filterDimId, setFilterDimId] = useState("");
    const [filterSysId, setFilterSysId] = useState("");

    // Create form
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [newDimId, setNewDimId] = useState("");
    const [newSysId, setNewSysId] = useState("");
    const [newFactor, setNewFactor] = useState("");
    const [newOffset, setNewOffset] = useState("");
    const [createError, setCreateError] = useState<string | null>(null);

    // Inline edit
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [editDesc, setEditDesc] = useState("");
    const [editDimId, setEditDimId] = useState("");
    const [editSysId, setEditSysId] = useState("");
    const [editFactor, setEditFactor] = useState("");
    const [editOffset, setEditOffset] = useState("");
    const [editError, setEditError] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Conversion tool
    const [convFrom, setConvFrom] = useState("");
    const [convTo, setConvTo] = useState("");
    const [convValue, setConvValue] = useState("");
    const [convResult, setConvResult] = useState<UnitConversionResult | null>(null);
    const [convError, setConvError] = useState<string | null>(null);
    const [converting, setConverting] = useState(false);

    useEffect(() => {
        loadMeta();
    }, [token]);

    async function loadMeta() {
        setLoading(true);
        setLoadError(null);
        try {
            const [dims, syss, unitList] = await Promise.all([
                getAllUnitDimensions(token),
                getAllUnitSystems(token),
                getAllUnits(token),
            ]);
            setDimensions(dims);
            setSystems(syss);
            setItems(unitList);
        } catch (err) {
            logApiError(err);
            setLoadError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function applyFilter() {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await getAllUnits(
                token,
                filterDimId || undefined,
                filterSysId || undefined,
            );
            setItems(data);
        } catch (err) {
            logApiError(err);
            setLoadError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function handleCreate() {
        if (!newName.trim()) { setCreateError("Name is required."); return; }
        if (!newDimId) { setCreateError("Dimension is required."); return; }
        if (!newSysId) { setCreateError("System is required."); return; }
        const factor = parseNum(newFactor);
        const offset = parseNum(newOffset);
        if (factor !== undefined && Number.isNaN(factor)) { setCreateError("Factor must be a number."); return; }
        if (offset !== undefined && Number.isNaN(offset)) { setCreateError("Offset must be a number."); return; }
        setCreateError(null);
        setLoading(true);
        try {
            const created = await createUnit(token, {
                name: newName.trim(),
                description: newDesc.trim() || undefined,
                unitDimensionId: newDimId,
                unitSystemId: newSysId,
                toBaseFactor: factor,
                toBaseOffset: offset,
            });
            setItems((prev) => [...prev, created]);
            setNewName("");
            setNewDesc("");
            setNewDimId("");
            setNewSysId("");
            setNewFactor("");
            setNewOffset("");
        } catch (err) {
            logApiError(err);
            setCreateError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    function startEdit(item: Unit) {
        setEditingId(item.id);
        setEditName(item.name);
        setEditDesc(item.description ?? "");
        setEditDimId(item.unitDimensionId);
        setEditSysId(item.unitSystemId);
        setEditFactor(item.toBaseFactor != null ? String(item.toBaseFactor) : "");
        setEditOffset(item.toBaseOffset != null ? String(item.toBaseOffset) : "");
        setEditError(null);
        setDeleteError(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditError(null);
    }

    async function handleSaveEdit(item: Unit) {
        if (!editDimId) { setEditError("Dimension is required."); return; }
        if (!editSysId) { setEditError("System is required."); return; }
        const factor = parseNum(editFactor);
        const offset = parseNum(editOffset);
        if (factor !== undefined && Number.isNaN(factor)) { setEditError("Factor must be a number."); return; }
        if (offset !== undefined && Number.isNaN(offset)) { setEditError("Offset must be a number."); return; }
        setEditError(null);
        setLoading(true);
        try {
            const updated = await updateUnit(token, {
                id: item.id,
                version: item.version,
                name: editName.trim() || undefined,
                description: editDesc.trim() || undefined,
                unitDimensionId: editDimId,
                unitSystemId: editSysId,
                toBaseFactor: factor ?? null,
                toBaseOffset: offset ?? null,
            });
            setItems((prev) => prev.map((x) => x.id === updated.id ? updated : x));
            setEditingId(null);
        } catch (err) {
            logApiError(err);
            setEditError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(item: Unit) {
        setDeleteError(null);
        setLoading(true);
        try {
            await deleteUnit(token, item.id, item.version);
            setItems((prev) => prev.filter((x) => x.id !== item.id));
            if (editingId === item.id) setEditingId(null);
        } catch (err) {
            logApiError(err);
            setDeleteError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function handleConvert() {
        setConvError(null);
        setConvResult(null);
        const v = Number(convValue);
        if (convValue.trim() === "" || Number.isNaN(v)) { setConvError("Enter a numeric value."); return; }
        if (!convFrom || !convTo) { setConvError("Select both From and To units."); return; }
        setConverting(true);
        try {
            setConvResult(await convertUnit(token, v, convFrom, convTo));
        } catch (err) {
            logApiError(err);
            setConvError(getUserMessage(err));
        } finally {
            setConverting(false);
        }
    }

    function dimName(id: string) {
        return dimensions.find((d) => d.id === id)?.name ?? id;
    }

    function unitName(id: string) {
        return items.find((u) => u.id === id)?.name ?? id;
    }

    function sysName(id: string) {
        return systems.find((s) => s.id === id)?.name ?? id;
    }

    return (
        <div className="unit-section">
            <h3>Units</h3>

            {loadError && <div className="error-message">{loadError}</div>}
            {loading && <div className="status-loading">Loading…</div>}

            {/* Filter */}
            <div className="unit-filter-row">
                <select value={filterDimId} onChange={(e) => setFilterDimId(e.target.value)} disabled={loading}>
                    <option value="">All Dimensions</option>
                    {dimensions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <select value={filterSysId} onChange={(e) => setFilterSysId(e.target.value)} disabled={loading}>
                    <option value="">All Systems</option>
                    {systems.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button onClick={applyFilter} disabled={loading}>Filter</button>
            </div>

            <ul className="unit-list">
                {items.map((item) => (
                    <li key={item.id} className="unit-row">
                        {editingId === item.id ? (
                            <div className="unit-edit-form">
                                <div className="unit-field-row">
                                    <label>Name</label>
                                    <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} disabled={loading} />
                                </div>
                                <div className="unit-field-row">
                                    <label>Description</label>
                                    <input type="text" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} disabled={loading} />
                                </div>
                                <div className="unit-field-row">
                                    <label>Dimension</label>
                                    <select value={editDimId} onChange={(e) => setEditDimId(e.target.value)} disabled={loading}>
                                        <option value="">-- select --</option>
                                        {dimensions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div className="unit-field-row">
                                    <label>System</label>
                                    <select value={editSysId} onChange={(e) => setEditSysId(e.target.value)} disabled={loading}>
                                        <option value="">-- select --</option>
                                        {systems.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div className="unit-field-row">
                                    <label>To-base factor</label>
                                    <input type="number" step="any" value={editFactor} onChange={(e) => setEditFactor(e.target.value)} placeholder="× factor" disabled={loading} />
                                </div>
                                <div className="unit-field-row">
                                    <label>To-base offset</label>
                                    <input type="number" step="any" value={editOffset} onChange={(e) => setEditOffset(e.target.value)} placeholder="+ offset" disabled={loading} />
                                </div>
                                <div className="unit-row-actions">
                                    <button onClick={() => handleSaveEdit(item)} disabled={loading}>Save</button>
                                    <button onClick={cancelEdit} disabled={loading} className="btn-secondary">Cancel</button>
                                </div>
                                {editError && <div className="error-message">{editError}</div>}
                            </div>
                        ) : (
                            <div className="unit-row-view">
                                <span className="unit-name">{item.name}</span>
                                {item.description && <span className="unit-desc">{item.description}</span>}
                                <span className="unit-meta">{dimName(item.unitDimensionId)} / {sysName(item.unitSystemId)}</span>
                                {(item.toBaseFactor != null || item.toBaseOffset != null) && (
                                    <span className="unit-meta">
                                        base = v×{item.toBaseFactor ?? 1}{item.toBaseOffset ? ` + ${item.toBaseOffset}` : ""}
                                    </span>
                                )}
                                <div className="unit-row-actions" style={{ marginLeft: "auto" }}>
                                    <button onClick={() => startEdit(item)} disabled={loading || !!editingId}>Edit</button>
                                    <button onClick={() => handleDelete(item)} disabled={loading || !!editingId}>Delete</button>
                                </div>
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {deleteError && <div className="error-message">{deleteError}</div>}

            {items.length === 0 && !loading && !loadError && (
                <p style={{ color: "var(--text-muted)" }}>No units found.</p>
            )}

            {/* Create form */}
            <div className="unit-create-form">
                <h4>Add Unit</h4>
                <div className="unit-field-row">
                    <label>Name</label>
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => { setNewName(e.target.value); setCreateError(null); }}
                        placeholder="e.g. Meter"
                        disabled={loading}
                    />
                </div>
                <div className="unit-field-row">
                    <label>Description</label>
                    <input
                        type="text"
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        placeholder="Optional description"
                        disabled={loading}
                    />
                </div>
                <div className="unit-field-row">
                    <label>Dimension</label>
                    <select value={newDimId} onChange={(e) => { setNewDimId(e.target.value); setCreateError(null); }} disabled={loading}>
                        <option value="">-- select --</option>
                        {dimensions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </div>
                <div className="unit-field-row">
                    <label>System</label>
                    <select value={newSysId} onChange={(e) => { setNewSysId(e.target.value); setCreateError(null); }} disabled={loading}>
                        <option value="">-- select --</option>
                        {systems.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <div className="unit-field-row">
                    <label>To-base factor</label>
                    <input type="number" step="any" value={newFactor} onChange={(e) => { setNewFactor(e.target.value); setCreateError(null); }} placeholder="× factor (e.g. 25.4)" disabled={loading} />
                </div>
                <div className="unit-field-row">
                    <label>To-base offset</label>
                    <input type="number" step="any" value={newOffset} onChange={(e) => { setNewOffset(e.target.value); setCreateError(null); }} placeholder="+ offset (e.g. 0)" disabled={loading} />
                </div>
                <button onClick={handleCreate} disabled={loading || !!editingId}>Add</button>
                {createError && <div className="error-message">{createError}</div>}
            </div>

            {/* Conversion tool */}
            <div className="unit-create-form" style={{ marginTop: "1.5rem" }}>
                <h4>Convert</h4>
                <div className="unit-field-row">
                    <label>Value</label>
                    <input
                        type="number"
                        step="any"
                        value={convValue}
                        onChange={(e) => { setConvValue(e.target.value); setConvError(null); }}
                        placeholder="e.g. 100"
                        disabled={converting}
                    />
                </div>
                <div className="unit-field-row">
                    <label>From</label>
                    <select value={convFrom} onChange={(e) => { setConvFrom(e.target.value); setConvTo(""); setConvResult(null); }} disabled={converting}>
                        <option value="">-- select --</option>
                        {items.map((u) => <option key={u.id} value={u.id}>{u.name} ({dimName(u.unitDimensionId)})</option>)}
                    </select>
                </div>
                <div className="unit-field-row">
                    <label>To</label>
                    <select value={convTo} onChange={(e) => { setConvTo(e.target.value); setConvResult(null); }} disabled={converting || !convFrom}>
                        <option value="">-- select --</option>
                        {(convFrom
                            ? items.filter((u) => u.unitDimensionId === items.find((f) => f.id === convFrom)?.unitDimensionId)
                            : items
                        ).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </div>
                <button onClick={handleConvert} disabled={converting}>{converting ? "Converting…" : "Convert"}</button>
                {convError && <div className="error-message">{convError}</div>}
                {convResult && (
                    <div className="status-selected" style={{ marginTop: "0.5rem" }}>
                        {convResult.inputValue} {unitName(convResult.fromUnitId)} = <strong>{convResult.convertedValue}</strong> {unitName(convResult.toUnitId)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UnitSection;
